import React, { useContext, useEffect, useState } from 'react';
import { Box, Checkbox, TextField, Typography } from '@material-ui/core';
import { HelpOutline } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import moment from 'moment';
import PropTypes from 'prop-types';

import Button from '~/components/Button';
import QuickBookRowAutocomplete from '~/components/QuickBookRow/QuickBookRowAutocomplete';
import QuickBookRowNotesAndAttachment from '~/components/QuickBookRow/QuickBookRowNotesAndAttachment';
import QuickBookRowScopeSelect from '~/components/QuickBookRow/QuickBookRowScopeSelect';
import {
  setInitialJournalBasedOnRecommendation,
  setUpJournalForSubmit,
  setUpRuleJournalScope,
} from '~/components/QuickBookRow/transactionTemplates/helpers/journalFunctions';
import { useStyles } from '~/components/QuickBookRow/transactionTemplates/styles';
import { getManualBasicJournal } from '~/components/SearchSelect/TransactionTypeOptions';
import { PersonContext } from '~/contexts/PersonContext';
import { create, find } from '~/feathersFunctionalWrapper';
import { checkIsSmallScreen } from '~/functions/ScreenSizeFunctions';
import { useSegmentTrack } from '~/functions/SegmentFunctions';
import { getBalanceSheetAccountOptions } from '~/helpers/utils/functionalAutocompleteLibrary';

import QuickBookRowLoading from '../QuickBookRowLoading';
export default function QuickBookRowTransferTemplate({ transaction, onTransactionAction, matchState, onGetHelp }) {
  const isMobile = checkIsSmallScreen();
  const tracking = useSegmentTrack();
  const { organizationId, organizationName } = useContext(PersonContext);
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [balanceSheetAccountOptions, setBalanceSheetAccountOptions] = useState([]);
  const [shouldBookMatchingTransaction, setShouldBookMatchingTransaction] = useState(false);
  const [matchingTransaction, setMatchingTransaction] = useState(null);
  const [journal, setJournal] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [shouldAddRule, setShouldAddRule] = useState(false);

  const findMatchingTransaction = async (journal, accountId) => {
    const transactionType = transaction.yodleeType === 'CREDIT' ? 'DEBIT' : 'CREDIT';

    const query = {
      organizationId: journal.organizationId,
      amount: journal.amount,
      yodleeType: transactionType,
      date: {
        $lte: moment(journal.date).add(3, 'days').format('YYYY-MM-DD'),
        $gte: moment(journal.date).subtract(3, 'days').format('YYYY-MM-DD'),
      },
      journalId: null,
      split: { $or: [false, null] },
      ignored: { $or: [false, null] },
      pending: false,
      deleted: false,
    };

    if (accountId) {
      query.accountId = accountId;
    } else {
      query.accountId = { $ne: transaction.account.id };
    }

    const result = await find('yodlee-transactions', { query });

    if (result.data.length === 1) {
      setShouldBookMatchingTransaction(true);
      setMatchingTransaction(result.data[0]);
      if (transactionType === 'CREDIT') {
        journal.debitLines[0].accountIdSelect = result.data[0].account;
      } else {
        journal.creditLines[0].accountIdSelect = result.data[0].account;
      }
    } else {
      setMatchingTransaction(null);
    }
  };

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    const getData = async () => {
      const balanceSheetAccounts = await getBalanceSheetAccountOptions(organizationId, {
        id: {
          $ne: transaction.account.id,
        },
      });

      setBalanceSheetAccountOptions(balanceSheetAccounts);

      const journalObject = getManualBasicJournal();
      const { account, id, description, date, amount, yodleeType } = transaction;

      const isCredit = yodleeType === 'CREDIT';
      const isDebit = yodleeType === 'DEBIT';

      if (isCredit) {
        journalObject.debitLines[0].accountIdSelect = account;
        journalObject.debitLines[0].yodleeTransactionId = id;
      } else if (isDebit) {
        journalObject.creditLines[0].accountIdSelect = account;
        journalObject.creditLines[0].yodleeTransactionId = id;
      }
      journalObject.organizationId = organizationId;
      journalObject.description = description;
      journalObject.date = date;
      journalObject.amount = amount;

      const hasMatch =
        matchState.importedTransactionRuleMatch.length === 1 &&
        matchState.importedTransactionRuleMatch[0].type.includes('Transfer');
      const hasV1Recommendation = !!transaction.recommendedAccountId;

      if (hasMatch) {
        const match = matchState.importedTransactionRuleMatch[0];
        if (isCredit) {
          journalObject.creditLines[0].accountIdSelect = balanceSheetAccounts.find(
            (option) => option.id === match.destinationAccountId,
          );
        } else if (isDebit) {
          journalObject.debitLines[0].accountIdSelect = balanceSheetAccounts.find(
            (option) => option.id === match.destinationAccountId,
          );
        }
        await setUpRuleJournalScope(match, journalObject, organizationName);
      } else if (hasV1Recommendation) {
        setInitialJournalBasedOnRecommendation({
          transaction,
          journalObject,
          accountOptions: balanceSheetAccounts,
          targetJournalLine: isCredit ? journalObject.creditLines[0] : journalObject.debitLines[0],
          organizationName,
        });
      } else {
        journalObject.journalScopeSelect = { name: `${organizationName} Portfolio`, id: 'Portfolio' };
      }

      await findMatchingTransaction(journalObject, null);
      setJournal(journalObject);
      setIsLoading(false);
    };
    getData();
  }, [balanceSheetAccountOptions, isLoading, journal, matchState, organizationId, organizationName, transaction]);

  const addTransfer = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    const journalData = {
      journal,
      organizationId,
      direction: transaction.yodleeType === 'CREDIT' ? 'cash in' : 'cash out',
      transactionId: transaction.id,
      type: 'Transfer',
      bookAsRefund: false,
    };

    try {
      const journalSubmit = setUpJournalForSubmit(journalData);

      // Find matching transaction if applicable
      if (matchingTransaction && shouldBookMatchingTransaction) {
        if (matchingTransaction.yodleeType === 'CREDIT') {
          journalSubmit.debitLines[0].yodleeTransactionId = matchingTransaction.id;
        } else if (matchingTransaction.yodleeType === 'DEBIT') {
          journalSubmit.creditLines[0].yodleeTransactionId = matchingTransaction.id;
        }
      }

      await create('journals', journalSubmit);
      setIsSubmitting(false);
      onTransactionAction(shouldAddRule, journalSubmit, matchState.descriptionMatches);
    } catch (err) {
      setError(err);
      setIsSubmitting(false);
      return;
    }
  };

  if (isLoading) {
    return <QuickBookRowLoading />;
  }

  return (
    <form onSubmit={addTransfer}>
      <Box className={classes.rootContainer}>
        <Box className={classes.container}>
          {journal.debitLines[0].yodleeTransactionId ? (
            <QuickBookRowAutocomplete
              className={classes.autoCompleteField}
              displaySubaccounts
              options={balanceSheetAccountOptions}
              onChange={(value) => {
                findMatchingTransaction(journal, value.id);
                setJournal({ ...journal, creditLines: [{ ...journal.creditLines[0], accountIdSelect: value }] });
              }}
              label="Transfer From Account"
              value={journal.creditLines[0].accountIdSelect}
              required
            />
          ) : (
            <QuickBookRowAutocomplete
              className={classes.autoCompleteField}
              displaySubaccounts
              options={balanceSheetAccountOptions}
              onChange={(value) => {
                findMatchingTransaction(journal, value.id);
                setJournal({ ...journal, debitLines: [{ ...journal.debitLines[0], accountIdSelect: value }] });
              }}
              label="Transfer To Account"
              value={journal.debitLines[0].accountIdSelect}
              required
            />
          )}
          <TextField
            className={isMobile ? classes.mobileDescriptionField : classes.descriptionFieldSixtySixPercent}
            label="Description"
            placeholder="Description"
            variant="outlined"
            size="small"
            value={journal.description}
            onChange={(event) => {
              setJournal({ ...journal, description: event.target.value });
            }}
          />
        </Box>
        <QuickBookRowScopeSelect journal={journal} setJournal={setJournal} />
        {matchingTransaction && (
          <Box>
            <Alert severity="info" className={classes.infoAlert}>
              <Box display="flex" flexDirection="column">
                <Typography variant="subtitle2" className={classes.matchingTransactionAlertText}>
                  We found a matching transaction. Leave the box checked to book it as well.
                </Typography>
                <Box marginRight="auto" display="flex" flexDirection="row" alignItems="center">
                  <Checkbox
                    color="primary"
                    checked={shouldBookMatchingTransaction}
                    onClick={() => {
                      setShouldBookMatchingTransaction(!shouldBookMatchingTransaction);
                    }}
                  />
                  {`${matchingTransaction.date} - ${matchingTransaction.description}`}
                </Box>
              </Box>
            </Alert>
          </Box>
        )}
        <QuickBookRowNotesAndAttachment
          journal={journal}
          onChange={(newJournal) => {
            setJournal(newJournal);
          }}
        />
        {error && error.message ? (
          <Alert severity="error" className={classes.errorAlert}>
            {error.message}
          </Alert>
        ) : null}
        <Box className={classes.actionsContainer}>
          <Button
            variant="outlined"
            size="small"
            className={classes.helpButton}
            onClick={() => {
              tracking('get_help clicked', { location: 'Inline Booking - Import Feed' });
              onGetHelp();
            }}
          >
            <HelpOutline className={classes.helpIcon} />
            Get Help
          </Button>
          <Box className={classes.rowActionsSecondaryContainer}>
            <Box className={classes.checkboxContainer}>
              <Checkbox
                checked={shouldAddRule}
                color="primary"
                onClick={() => {
                  tracking('create_rule clicked', { location: 'Inline Booking - Import Feed' });
                  setShouldAddRule(!shouldAddRule);
                }}
              />
              <Typography variant="body2">Create rule after saving</Typography>
            </Box>
            <Button
              variant="contained"
              className={classes.saveButton}
              size="small"
              type="submit"
              onClick={() => {
                tracking('save clicked', { location: 'import_feed rei' });
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </form>
  );
}

QuickBookRowTransferTemplate.propTypes = {
  transaction: PropTypes.object.isRequired,
  onTransactionAction: PropTypes.func,
  onGetHelp: PropTypes.func.isRequired,
  matchState: PropTypes.object.isRequired,
};
